/* eslint-disable @typescript-eslint/no-explicit-any */
import { ColDef, GetRowIdParams, GridReadyEvent, RowSelectedEvent } from '@ag-grid-community/core';
import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AgFns,
  AgInfoTooltip,
  AvailableStatusDialogComponent,
  GridOptionsExt,
  IAvailableStatusDto,
  ISortModel,
  ProductFns,
  ProxAgFns,
  QueryComponent,
  ResponsiveColDef,
  StatusService,
} from '@core';
import {
  FeatureChoice,
  PricedProductType,
  PricedProductTypeFeatureChoice,
  ProductTypeConfigProduct,
  ProductType,
  ProductTypeConfig,
  ProductTypeConfigAddon,
  Supplier,
  StockCondition,
  Account,
} from '@models';
import { BaseService } from '@core';
import { UtilFns } from '@utils';
import * as _ from 'lodash';

import { AccountDbQueryService } from '../services/account-db-query.service';
import { DialogService } from 'primeng/dynamicdialog';

@Component({
  selector: 'prox-price-lists',
  templateUrl: './price-lists.component.html',
  standalone: false,
})
export class PriceListsComponent extends QueryComponent {
  accountId!: string;
  account?: Account;
  pptGridOptions!: GridOptionsExt;
  selectedPpt?: PricedProductType;
  pptMap!: Map<string, PricedProductType>; // key is the ProductTypeId
  pptfcMap!: Map<FeatureChoice, PricedProductTypeFeatureChoice>;
  ptcs: ProductTypeConfig[] = [];
  selectedPtc?: ProductTypeConfig;
  ptcpGridOptions!: GridOptionsExt;
  ptcps: ProductTypeConfigProduct[] = [];
  ptcaGridOptions!: GridOptionsExt;
  ptcas: ProductTypeConfigAddon[] = [];
  supplierSet = new Set<Supplier>();
  availableStatusDtos: IAvailableStatusDto[] = [];

  constructor(
    baseService: BaseService,
    route: ActivatedRoute,
    override dbQueryService: AccountDbQueryService,
    private statusService: StatusService,
    private pngDialogService: DialogService,
  ) {
    super(baseService, route, dbQueryService);
  }

  override async updateFromParams(params: object): Promise<void> {
    this.accountId = params['accountId'];
    UtilFns.assertNonEmptyString(this.accountId, 'accountId');
    this.account = await this.dbQueryService.getAccountById(this.accountId);
    await this.dbQueryService.getAll(StockCondition);
    this.pptGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onPptGridReady,
      onRowSelected: this.onPptRowSelected,
      rowModelType: 'serverSide',
    });
    AgFns.captureGridRouteParams(this.pptGridOptions, this.route, 'id');

    // ------------------------------------------

    this.ptcpGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onPtcpGridReady,
      masterDetail: true,
      getRowId: (rowIdParams: GetRowIdParams) => {
        const pc = rowIdParams.data as ProductTypeConfigProduct;
        return pc.productId;
      },
    });

    this.ptcaGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onPtcaGridReady,
      masterDetail: true,
      getRowId: (rowIdParams: GetRowIdParams) => {
        const x = rowIdParams.data as ProductTypeConfigAddon;
        return x.pricedAddonId;
      },
    });

    this.setTitle('Product Catalog for ' + this.account.name);
    this.isPageReady = true;
  }

  // ---------------------------------------------------------------------------------

  onPptGridReady(event: GridReadyEvent) {
    const gridOptions = event.context.gridOptions;
    const [colDefs, sortModel] = this.getPptColDefsAndSortModel();
    // Maybe later
    // this.updatePptMasterDetail(gridOptions);

    AgFns.initGrid(event, colDefs, sortModel);
    AgFns.setupResponsiveColumns(this.pptGridOptions, colDefs);
    this.updateDatasource();
  }

  getPptColDefsAndSortModel() {
    const colDefs: ResponsiveColDef[] = [
      {
        field: 'productType.supplier.name',
        headerName: 'Supplier',
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(100),
      },
      {
        field: 'productType.manufacturer.name',
        headerName: 'Manufacturer',
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(100),
      },
      // {
      //   field: 'productType.productKey',
      //   headerName: 'SKU',
      //   filter: 'agTextColumnFilter',
      // },
      {
        field: 'productType.name',
        headerName: 'Product Name',
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(150),
      },
      {
        field: 'productType.description',
        headerName: 'Description',
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(150),
        hideOnLt2xl: true,
      },
      {
        ...AgFns.createIconProps('Available?', '', this.showStatusReporter.bind(this), {
          label: '',
          getIcon: this.getIcon.bind(this),
        }),
        cellStyle: { 'justify-content': 'center', display: 'flex' },
        sortable: false,
        ...AgFns.fixWidth(100),
        suppressHeaderMenuButton: true,
      },
      {
        headerName: '# Configs',
        filter: 'agNumberColumFilter',
        colId: 'configCount',
        type: 'numericColumn',
        ...AgFns.fixWidth(130),
        valueGetter: (params) => {
          const ppt = params.data as PricedProductType;
          if (ppt == null) return;
          return ppt.productTypeConfigs.length;
        },
        sortable: false,
        hideOnLt2xl: true,
      },
      { field: 'basePrice', headerName: 'Base Price', filter: 'agNumberColumnFilter', ...AgFns.fixWidth(120) },
      {
        field: 'productType.leadTimeDays',
        headerName: 'Lead Time (Days)',
        filter: 'agNumberColumnFilter',
        ...AgFns.fixWidth(160),
        hideOnLt2xl: true,
      },
    ];
    const sortModel: ISortModel = [
      { colId: 'productType.manufacturer.name', sort: 'asc' },
      { colId: 'productType.name', sort: 'asc' },
      { colId: 'productType.description', sort: 'asc' },
    ];
    return [colDefs, sortModel] as const;
  }

  getIcon(entity: PricedProductType | ProductTypeConfigProduct): string {
    return this.statusService.getStatusIcon(entity);
  }

  async showStatusReporter(entity: PricedProductType) {
    const result = await AvailableStatusDialogComponent.open(
      this.pngDialogService,
      {
        entity: entity,
        subjectTypeName: 'Priced Product Type',
      },
      {
        header: 'Product Type Status Detail',
      },
    );
  }

  async onPptRowSelected(event: RowSelectedEvent) {
    if (!event.node.isSelected()) return;
    const ppt = event.data as PricedProductType;
    this.selectedPpt = ppt;
    if (ppt == null) return;
    await UtilFns.waitAssign(
      (x) => (this.ptcs = x),
      _.sortBy(this.selectedPpt?.productTypeConfigs || [], (ptc) => ptc.name),
    );
    // Removed because of hack used above to avold PrimeNg deep-equals bug.
    // this.ptcs = _.sortBy(
    //   this.selectedPpt?.productTypeConfigs || [],
    //   (ptc) => ptc.name
    // );
    this.selectedPtc = _.first(this.ptcs);
    this.onSelectedPtcChange();
    this.updateGridRouteParams(this.pptGridOptions, ppt.id);
  }

  async updateDatasource() {
    if (this.pptGridOptions.apiX == null) return;
    const returnFn = async (ppts: PricedProductType[]) => {
      const promises: Promise<any>[] = [];

      const newSupplierSet = new Set(ppts.map((ppt) => ppt.productType.supplier));
      newSupplierSet.forEach((s) => {
        if (!this.supplierSet.has(s)) {
          this.supplierSet.add(s);
          promises.push(...this.fetchSupplierInfo(s.id));
        }
      });
      await Promise.all(promises);
    };
    const ds = AgFns.buildDatasource(() => this.dbQueryService.createPricedProductTypesWithConfigByAccountQuery(this.accountId), 'id', {
      returnFn,
    });
    this.pptGridOptions.apiX.setGridOption('serverSideDatasource', ds);
    AgFns.applyGridRouteParams(this.pptGridOptions);
  }

  fetchSupplierInfo(supplierId: string) {
    return [this.dbQueryService.getAddons(supplierId)];
  }

  // Ptc related - PTC dropdown

  async onSelectedPtcChange() {
    const ptc = this.selectedPtc;
    UtilFns.wait(0);
    if (ptc == null) {
      this.ptcps = [];
      return;
    } else {
      this.lookupPrices(ptc);
      this.ptcps = ptc.productTypeConfigProducts;
      this.ptcas = ptc.productTypeConfigAddons;
    }
    const [colDefs, _] = this.getPtcpColDefsAndSortModel(ptc.pricedProductType.productType);
    this.ptcpGridOptions.apiX?.setGridOption('columnDefs', colDefs);
    AgFns.refreshGrid(this.ptcpGridOptions, this.ptcps);
  }

  lookupPrices(ptc: ProductTypeConfig) {
    ptc.productTypeConfigProducts.forEach((pc) => {
      const fcIdSet = new Set(pc.product.productFeatureChoices.map((x) => x.featureChoiceId));
      pc._pricedProductTypeFeatureChoices = pc.productTypeConfig.pricedProductType.pricedProductTypeFeatureChoices.filter((pptfc) =>
        fcIdSet.has(pptfc.featureChoiceId),
      );
    });
  }

  // --------------------------------------------------------------------

  onPtcpGridReady(event: GridReadyEvent) {
    const [colDefs, sortModel] = this.getPtcpColDefsAndSortModel();
    AgFns.initGrid(event, colDefs, sortModel);
  }

  getPtcpColDefsAndSortModel(productType?: ProductType) {
    const colDefs: ColDef[] = [
      {
        headerName: '',
        cellRenderer: 'agGroupCellRenderer',
        ...AgFns.fixWidth(40),
        suppressHeaderMenuButton: true,
      },
      ProxAgFns.getProductSortOrderColDef((x) => x.data.product),

      ...ProductFns.getColDefsForProductType(productType, (params) => {
        return (params.data as ProductTypeConfigProduct).product;
      }),

      // { headerName: 'SKU', field: 'product.productType.productKey' },
      {
        headerName: 'Configuration / Catalog Item',
        field: 'productTypeConfig.name',
        editable: false,
        filter: 'agTextColumnFilter',
        colId: 'configuration',
        ...AgFns.flexWidth(150),
      },
      {
        ...AgFns.createIconProps('Available?', '', this.showProductStatus.bind(this), { label: '', getIcon: this.getIcon.bind(this) }),
        sortable: false,
        cellStyle: { 'justify-content': 'center', display: 'flex' },
        ...AgFns.fixWidth(120),
      },
      {
        headerName: 'Base Price',
        field: 'productTypeConfig.pricedProductType.basePrice',
        editable: false,
        filter: 'agNumberColumnFilter',
        colId: 'basePrice',
        ...AgFns.fixWidth(130),
      },
      {
        headerName: 'Feature Choice Price',
        filter: 'agNumberColumFilter',
        colId: 'ExtraPrice',
        ...AgFns.fixWidth(160),
        valueGetter: (params) => {
          const pc = params.data as ProductTypeConfigProduct;
          if (pc == null) return;
          const extraPrice = _.sum(pc._pricedProductTypeFeatureChoices?.map((pptfc) => pptfc.price));
          return extraPrice;
        },
        cellRenderer: AgFns.infoTooltipCellRenderer,
        tooltipValueGetter: (params) => {
          params.context = 'Feature Pricing';
          const pc = params.data as ProductTypeConfigProduct;
          if (pc == null) return;
          const lines = pc._pricedProductTypeFeatureChoices?.map((pptfc) => {
            return pptfc.featureChoice.feature.name + ' : ' + pptfc.featureChoice.validValue + ' = ' + UtilFns.fmtCurrency(pptfc.price);
          });
          return lines?.join('</br>');
        },
        tooltipComponent: AgInfoTooltip,
        ...AgFns.fixWidth(160),
      },
      {
        headerName: 'Add-Ons Price',
        filter: 'agNumberColumFilter',
        colId: 'RAddonsPrice',
        valueGetter: (params) => {
          const ptcp = params.data as ProductTypeConfigProduct;
          if (ptcp == null) return;
          const price = _.sum(
            ptcp.productTypeConfig.productTypeConfigAddons
              .filter((ptcAddon) => !ptcAddon.isOptional)
              .map((ptcAddon) => ptcAddon.pricedAddon.price),
          );
          return price;
        },
        ...AgFns.fixWidth(180),
      },
    ];
    const sortModel: ISortModel = [{ colId: 'productSortOrder', sort: 'asc' }];
    AgFns.updateColDefs(colDefs);
    return [colDefs, sortModel] as const;
  }

  async showProductStatus(entity: ProductTypeConfigProduct) {
    const result = await AvailableStatusDialogComponent.open(
      this.pngDialogService,
      {
        entity: entity,
        subjectTypeName: 'Product',
      },
      {
        header: 'Product Status Detail',
      },
    );
  }

  onPtcaGridReady(event: GridReadyEvent) {
    const [colDefs, sortModel] = this.getPtcaColDefsAndSortModel();
    AgFns.initGrid(event, colDefs, sortModel);
  }

  getPtcaColDefsAndSortModel() {
    const colDefs = [
      {
        field: 'pricedAddon.name',
        headerName: 'Add-On Name',
        editable: false,
        ...AgFns.flexWidth(250),
      },
      {
        headerName: 'Price',
        colId: 'Price',
        ...AgFns.fixWidth(100),
        valueGetter: (params) => {
          const ptcAddon = params.data as ProductTypeConfigAddon;
          if (ptcAddon == null) return;
          const paddon = ptcAddon.pricedAddon;
          const price = paddon.price;
          return price;
        },
      },
    ];
    const sortModel: ISortModel = [{ colId: 'pricedAddon.name', sort: 'asc' }];
    AgFns.updateColDefs(colDefs);
    return [colDefs, sortModel] as const;
  }

  // ---------------------------------------------
}
